import React from 'react';
import { ViewProduct, ViewProducts } from './models/ViewProduct.model';
import { ViewVariant, ShopifyVariant } from './models/ShopifyVariant.model';
import { Preorder } from './models/Preorder.model';
import { VariantStatus } from './enums/VariantStatus.enum';
import { ShopifyCurrency } from './models/ShopifyCurrency.model';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import timezone from 'dayjs/plugin/timezone';
import { Color } from './enums/Color.enum';
import { ProductHandle } from './enums/ProductHandle.enum';
import { HoneyloveWindow } from './models/HoneyloveWindow';
import vhCheck from 'vh-check';
import { BundleConfig } from './hooks/bundleConfiguration';
import { Product } from 'schema-dts';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);

export interface KVP<T> {
  [key: string]: T;
}

declare const window: HoneyloveWindow;
const buildAssetPrefix = (url: string) => url.replace(/(\/assets\/)(.*)/, '$1');
const buildAssetSuffix = (url: string) => (url.match(/.*(\?.*)$/) ? url.replace(/.*(\?.*)$/, '$1') : '');
export function uniqueArray<T>(items: T[]): T[] {
  return items.reduce((acc, current) => {
    return current && !acc.includes(current) ? [...acc, current] : acc;
  }, [] as T[]);
}

export const buildAssetPathFn =
  (url: string) =>
  (file: string): string =>
    `${buildAssetPrefix(url)}${file}${buildAssetSuffix(url)}`;
// default to "/" as in SSR the static assets will be available at the root (referencing files in /public in the repo)
export const assetPathFn = buildAssetPathFn(typeof window !== 'undefined' ? window?.Honeylove?.environment?.assetUrl : '/');

export const hashValues = (): KVP<string> =>
  window.location.hash
    .substring(1)
    .split('&')
    .reduce((acc, pair) => {
      const [key, value] = pair.split('=');
      if (key) {
        acc[decodeURIComponent(key)] = decodeURIComponent(value);
      }
      return acc;
    }, {} as KVP<string>);
export const getHashVariable = (name: string): string | undefined => hashValues()[name];
export const setHashVariable = (key: string, value: string): void => {
  const isHomepage = window.location.pathname.length < 2;
  const hashes = hashValues();
  const hasValue = typeof value !== 'undefined' && value !== null;
  hashes[key] = value;
  const hashArray = Object.keys(hashes).reduce((acc: string[], k) => {
    acc.push(`${k}=${hashes[k]}`);
    return acc;
  }, []);
  if (!isHomepage && hasValue) {
    history.replaceState(undefined, '', `#${hashArray.join('&')}`);
  }
};
export const clearHashVariable = (key: string): void => {
  const hashes = hashValues();
  const hashArray = Object.keys(hashes)
    .filter((k) => k !== key)
    .reduce((acc, k) => {
      acc.push(`${k}=${hashes[k]}`);
      return acc;
    }, [] as string[]);

  history.replaceState(undefined, '', `#${hashArray.join('&')}`); // For some reason we have to keep the # even if everything else is empty
};

export const setProductPath = (product: ViewProduct, color?: string, size?: string): void => {
  const paramString = buildParams({ color, size });

  const { handle } = product;
  history.pushState({ handle }, handle, `/products/${handle}${paramString}`);
  document.title = `${product.title} - Honeylove`;
};

export const setLanderPath = (product: ViewProduct): void => {
  const { handle } = product;
  const path = `/pages/${handle}`;
  history.pushState({ handle }, handle, path);
  document.title = `${product.title} - Honeylove`;
};

export const buildParams = (params: KVP<string | undefined>) => {
  const paramString = Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null)
    .map((key) => `${key}=${params[key]}`)
    .join('&');
  return !!paramString ? `?${paramString}` : '';
};

export const getURLParam = <T>(name: string): T | null => {
  if (typeof window !== 'undefined') {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return (urlParams.get(name) as unknown as T) ?? null;
  }

  return null;
};

export const setURLParam = (name: string, value?: string): void => {
  if (value !== undefined && value !== null) {
    const params = new URLSearchParams(window.location.search);
    params.set(name, value);
    window.history.replaceState({ name }, value, decodeURIComponent(`${window.location.pathname}?${params}`));
  }
};

export const getPreorder = (preorders: Preorder[], variant: ViewVariant): Preorder | undefined => {
  const { sku } = variant || {};
  return (preorders || []).find((p) => p.sku.toLowerCase() === (sku || '').toLowerCase());
};

export const variantStatus = (preorder: Preorder | undefined, variant: ViewVariant | ShopifyVariant): VariantStatus => {
  if (!variant) {
    return VariantStatus.NoVariant;
  } else if (!variant.available) {
    return VariantStatus.SoldOut;
  } else if (!variant.skuAvailability && preorder?.isPreorder && preorder.preorderText) {
    return VariantStatus.PreOrder;
  } else if (variant.available) {
    return VariantStatus.Available;
  }

  return VariantStatus.NoVariant;
};

export const scrollToRef = (ref: React.RefObject<HTMLElement>, offset = 0): void => {
  const scrollContainer = hlViewportLocked() ? document.querySelector('.hl-viewport') : (window as unknown as HTMLElement);
  ref?.current && scrollContainer?.scrollTo(0, ref.current.offsetTop + offset);
};

export const scrollToTop = (): void => {
  const scrollContainer = hlViewportLocked() ? document.querySelector('.hl-viewport') : (window as unknown as HTMLElement);
  scrollContainer?.scrollTo(0, 0);
};

export const hlViewportLocked = (): boolean => {
  const viewportEl = document.querySelector('.hl-viewport') as unknown as HTMLElement;
  return viewportEl ? getComputedStyle(viewportEl).overflow === 'auto' : false;
};

export const sizeImage = (imageUrl: string, modifier: string): string => imageUrl?.replace(/^(.*)(\.jpg.*|\.png.*)/, `$1_${modifier}$2`);

export const titleToParts = (title: string): string[] => {
  const titleMatcher = title.match(/^([A-z0-9]*)\s(.*)/);
  const titlePart1 = titleMatcher ? titleMatcher[1] : '';
  const titlePart2 = titleMatcher ? titleMatcher[2] : '';
  return [titlePart1 || title, titlePart2];
};

export const getCookie = (cookieName: string): string => {
  if (typeof window !== 'undefined') {
    const name = cookieName + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let c of ca) {
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
  }
  return '';
};

export const clearCookie = (cookieName: string): void => setCookie(cookieName, '', true);
export const setCookie = (cookieName: string, value: string, clear = false): void => {
  // clear immediately or clear in 24 hours
  const expiration = clear ? 'expires=Thu, 01 Jan 1970 00:00:01 GMT' : `expires=${new Date(new Date().getTime() + 24 * 60 * 60 * 1000)}`;
  document.cookie = `${cookieName}=${encodeURIComponent(value)}; domain=.honeylove.com; SameSite=Strict; Path=/;${expiration}`;
};

export const bannerCookieName = 'hlBanner-v2';
export const showMarketingMessage = (messages: string[]): void => {
  setCookie(bannerCookieName, JSON.stringify(messages));
};

export const iconAbbreviation = (product: ViewProduct): string => {
  const handleMap: { [handle in ProductHandle]: string } = {
    [ProductHandle.SuperPowerBrief]: 'spb',
    [ProductHandle.SuperPowerShort]: 'sps',
    [ProductHandle.SuperPowerShortV2]: 'sps',
    [ProductHandle.QueenBrief]: 'qb',
    [ProductHandle.SuperPowerThong]: 'spt',
    [ProductHandle.LiftWearTank]: 'lwt',
    [ProductHandle.LiftWearCami]: 'lwc',
    [ProductHandle.SilhouetteBra]: 'lwsb',
    [ProductHandle.CrossoverBra]: 'lwbr03',
    [ProductHandle.BoldnessTank]: 'bst01',
    [ProductHandle.EGiftCard]: 'egc',
    [ProductHandle.TapeMeasure]: 'hlmt',
    [ProductHandle.IntimatesBag]: 'hlwb',
    [ProductHandle.VNeckBra]: 'vnb',
    [ProductHandle.WaistHeroCincher]: 'whc',
    [ProductHandle.EverReadyPant]: 'erp',
    [ProductHandle.CamiBodysuit]: 'lwcb',
    [ProductHandle.EverydayCottonBrief]: 'ecb',
    [ProductHandle.EverydayCottonThong]: 'ect',
    [ProductHandle.LeggingTwo]: 'lt',
    [ProductHandle.BoldnessBodysuit]: 'bb',
    [ProductHandle.SilhouetteBrief]: 'sb',
    [ProductHandle.TankBodysuit]: 'lwtb',
    [ProductHandle.SculptwaveCapri]: 'swc',
  };

  return handleMap[product.handle];
};

export const currencyToString = (currency: ShopifyCurrency, value: number, fractionDigits = 2): string =>
  Number(value).toLocaleString(getLocale(), {
    style: 'currency',
    currency: currency.active,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });

export const toViewPrice =
  (currency: ShopifyCurrency) =>
  (priceInCents: number, maximumFractionDigits = 2): string =>
    currencyToString(currency, priceInCents, maximumFractionDigits);

const getLocale = () => {
  if (typeof window !== 'undefined') {
    return (navigator.languages && navigator.languages[0]) || navigator.language || 'en-US';
  }

  return 'en-US';
};

export const poll = (readyFn: () => boolean, timeout = 2000, interval = 100): Promise<any> => {
  const endTime = Number(new Date()) + timeout;
  const checkCondition = (resolve: (result: any) => void, reject: (reason: any) => void) => {
    // If the condition is met, we're done!
    const result = readyFn();
    if (result) {
      resolve(result);
    }
    // If the condition isn't met but the timeout hasn't elapsed, go again
    else if (Number(new Date()) < endTime) {
      setTimeout(checkCondition, interval, resolve, reject);
    }
    // Didn't match and too much time, reject!
    else {
      reject(new Error(`Polling timed out for ${readyFn}`));
    }
  };

  return new Promise(checkCondition);
};

const imageFilter = (color: Color) => (imageUrl: string) => !color || imageUrl.includes(color.replace(/\s/g, '_'));
export const filterImagesByColor = (color: Color, images: string[]): string[] => images.filter(imageFilter(color));

export const isMoreThan7DaysAway = (date: Dayjs): boolean => {
  return dayjs().add(7, 'day').isBefore(date);
};

export const howManyDaysAwayFromToday = (date: Dayjs): number => {
  const now = dayjs();
  return date.diff(now, 'day');
};

export const isAfterToday = (targetDate: Dayjs): boolean => {
  const now = dayjs();
  return !targetDate.isToday() && targetDate.isAfter(now);
};

export const isAfterLocalTomorrow = (targetDate: Dayjs): boolean => {
  const now = dayjs();

  if (now.isAfter(targetDate) || targetDate.isToday() || targetDate.isTomorrow()) {
    return false;
  }

  return true;
};

export const parseStringAsAmericaLosAngelesTimezone = (dateTime: string): Dayjs => dayjs.tz(dateTime, 'America/Los_Angeles');

export const prettyPrintDateStr = (dateTime: string | undefined): string => {
  if (!dateTime) {
    return '';
  }
  return dayjs(dateTime).format('MMM D');
};

const italicizedWords = ['LiftWear', 'SuperPower'];

export const breakLines = (line: string): string => {
  for (const word of italicizedWords) {
    line = line.replace(word, `<i>${word}</i>`);
  }

  return `${line}<br/>`;
};

export const isHoneyloveDomain = (url: string): boolean => {
  return !!url.match(/https?:\/\/[a-z0-9]*\.?honeylove.com?.*/i);
};

export const titleCase = (input: string): string => {
  return input
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return word
        .replace(word[0], word[0].toUpperCase())
        .replace(/superpower/gi, 'SuperPower')
        .replace(/softsculpt/gi, 'SoftSculpt')
        .replace(/liftwear/gi, 'LiftWear');
    })
    .join(' ');
};

export const productTitlePart1 = (title: string): string => {
  const startIndex = title.match(/softsculpt/gi) ? 1 : 0;
  return title.split(' ')[startIndex];
};
export const productTitlePart2 = (title: string): string => {
  const startIndex = title.match(/softsculpt/gi) ? 2 : 1;
  return title.split(' ').slice(startIndex).join(' ');
};

export const preloadImage = (assetName: string): void => {
  const image = new Image();
  image.src = assetPathFn(assetName);
};

export const SIZE_TO_FILTER_BY = 'Medium';

export const cleanPrice = (value: string): string => value.replace('.00', '');
export const numToWords = (value: number): string | undefined => {
  switch (value) {
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
    case 5:
      return 'five';
    case 6:
      return 'six';
    case 7:
      return 'seven';
    case 8:
      return 'eight';
    case 9:
      return 'nine';
    default:
      return undefined;
  }
};

export const getSwimlane = (lanes = 2): number => {
  const currentTime = new Date().getTime();
  const swimlaneVisitTime = Number(localStorage.getItem('hl-swimlane-visit-time')) || currentTime;
  const swimlaneLastTime = Number(localStorage.getItem('hl-swimlane-last-time'));
  // set the localStorage value if first visit or more than 2 weeks since last active
  if (currentTime - swimlaneLastTime > 86400 * 14 * 1000) {
    localStorage.setItem('hl-swimlane-visit-time', String(currentTime));
  }
  localStorage.setItem('hl-swimlane-last-time', String(currentTime));
  return (swimlaneVisitTime % lanes) + 1;
};

export const encodeBase64Url = (value: string): string => {
  // ASCII strings only!
  return btoa(value).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
};

export const decodeBase64Url = (value: string): string => {
  try {
    return atob(value.replace(/\-/g, '+').replace(/_/g, '/'));
  } catch {
    return '';
  }
};

export const cleanPhone = (identifier: string) => identifier.replace(/^(\+)|\D/g, '$1');

export const navigate = (url: string): void => {
  window.location.href = url;
};

export const getProductUrl = (handle: ProductHandle): string => {
  return `/products/${handle}`;
};

export const isKeyboardEvent = (event: React.SyntheticEvent): event is React.KeyboardEvent => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((event as unknown as any)['key']) {
    return true;
  }
  return false;
};

export const ifEnter = (event: React.KeyboardEvent, callback: () => void): void => {
  if (event.key === 'Enter') {
    callback?.();
  }
};

export const preventDefaultScroll = (e: Event): void => e.preventDefault();
export const allowScrollWithoutPropogation = (e: Event): void => e.stopPropagation();
export const lockParentWithScrollingChild = (): void => {
  document.querySelector('html')?.classList.add('hl-body-locked');
  document.querySelector('body')?.classList.add('hl-body-locked');
  document.querySelector('.hl-viewport')?.classList.add('hl-body-locked');
};

export const unlockParentWithScrollingChild = (): void => {
  document.querySelector('html')?.classList.remove('hl-body-locked');
  document.querySelector('body')?.classList.remove('hl-body-locked');
  document.querySelector('.hl-viewport')?.classList.remove('hl-body-locked');
};

export const isRecoilProps = <T>(props: unknown): props is T => {
  const keys = Object.keys(props as T) as (keyof T)[];
  return keys.includes('selectionType' as keyof T) && keys.includes('selectionKey' as keyof T);
};

export const getUniqueColorsForBundleConfig = (productColorCombos: BundleConfig['productColorCombos'], swatch: string): Color[] => {
  return (
    productColorCombos
      .find((pcc) => pcc.swatchTitle === swatch)
      ?.colorCombos.reduce<Color[]>((acc, curr) => {
        return [...acc, ...Array.from({ length: curr.quantity }).map((_) => curr.color)];
      }, []) ?? []
  );
};

export const productHandleFromViewVariant = (variant: ViewVariant, allProducts: ViewProducts): ProductHandle | undefined => {
  return Object.values(allProducts).find((p) => p.variants.some((v) => v.id === variant.id))?.handle;
};

export const productHandleToDiscountPercent: Partial<Record<ProductHandle, number>> = {
  'superpower-short': 20,
  'superpower-brief': 20,
  'superpower-thong': 20,
  'queen-brief': 20,
  'liftwear-tank': 25,
  'liftwear-cami': 25,
  'boldness-tank': 25,
  'v-neck-bra': 15,
  'silhouette-bra': 15,
  'legging-two': 25,
  'everready-pant': 25,
  'cami-bodysuit': 20,
  'liftwear-tank-bodysuit': 20,
  'boldness-bodysuit': 20,
  'silhouette-brief': 10,
};
